import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import { useDeleteGroupApi, useGroupsApi } from "hooks/use-distribution-api";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DistributionTabs } from "routes/admin/distribution/components/DistributionTabs";

export const GroupsPage: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, loading, error, refetch } = useGroupsApi({
    page,
    limit: 20,
    search: search || undefined,
  });
  const { deleteGroup } = useDeleteGroupApi();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (groupId: string) => {
    setActionLoading(true);
    try {
      await deleteGroup(groupId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>配信グループ管理</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-2">
        <PageTitle>配信先管理</PageTitle>
      </div>
      <DistributionTabs
        active="groups"
        actions={
          <Button
            type="button"
            size="sm"
            onClick={() => navigate("/admin/distribution/groups/new")}
          >
            + 新規作成
          </Button>
        }
      />

      {/* 検索 */}
      <div className="mb-6 flex gap-2">
        <input
          type="text"
          placeholder="グループ名で検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered input-sm w-full md:w-96"
        />
        <Button type="button" onClick={handleSearch}>
          検索
        </Button>
      </div>

      {/* テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>グループ名</Table.HeaderCell>
              <Table.HeaderCell>連絡先数</Table.HeaderCell>
              <Table.HeaderCell>作成者</Table.HeaderCell>
              <Table.HeaderCell>作成日</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-8 text-base-content/60"
                >
                  グループがありません
                </td>
              </tr>
            ) : (
              data?.items.map(group => (
                <Table.Row key={group.id}>
                  <Table.Cell>
                    {group.isDefault && (
                      <StatusBadge tone="soft-warning">デフォルト</StatusBadge>
                    )}
                    <Link to={`/admin/distribution/groups/${group.id}`}>
                      {group.name}
                    </Link>
                    {group.description && (
                      <div className="text-base-content/60 mt-1">
                        {group.description}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone="soft-neutral">
                      {group.contactCount}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>{group.createdBy.name}</Table.Cell>
                  <Table.Cell>
                    {new Date(group.createdAt).toLocaleDateString("ja-JP")}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() =>
                          navigate(
                            `/admin/distribution/groups/${group.id}/edit`,
                          )
                        }
                      >
                        編集
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setDeleteConfirm(group.id)}
                        variant="destructive"
                        size="xs"
                      >
                        削除
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button type="button" size="sm" isJoinItem>
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">グループを削除しますか？</h3>
            <p className="py-4">
              この操作は取り消せません。グループ内の連絡先との紐付けも削除されます。
            </p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loading size="sm" /> : "削除"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};
